const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/
const GTM_SCRIPT_ATTR = 'data-google-tag-manager'
const GTM_EVENT_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/

let initializedContainerId = null
let routeTrackingInstalled = false

export function isValidGtmContainerId(value) {
  return typeof value === 'string' && GTM_ID_PATTERN.test(value.trim())
}

export function isGtmActive() {
  return Boolean(initializedContainerId)
}

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

function findInjectedGtmScript() {
  if (typeof document === 'undefined') {
    return null
  }

  return document.querySelector(`script[${GTM_SCRIPT_ATTR}]`)
}

function readExistingContainerId() {
  const existing = findInjectedGtmScript()
  return existing?.getAttribute(GTM_SCRIPT_ATTR) || null
}

/**
 * Load Google Tag Manager from a tenant Container ID (e.g. GTM-ABC1234).
 *
 * noscript iframe is omitted on purpose: the ID comes from a JS API response,
 * so a hardcoded <noscript> snippet in index.html cannot be used.
 */
export function initGoogleTagManager(containerId) {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return null
    }

    const existingId = initializedContainerId || readExistingContainerId()
    if (existingId) {
      initializedContainerId = existingId
      return initializedContainerId
    }

    if (!isValidGtmContainerId(containerId)) {
      return null
    }

    const id = containerId.trim()

    ensureDataLayer().push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`
    script.setAttribute(GTM_SCRIPT_ATTR, id)
    document.head.appendChild(script)

    initializedContainerId = id
    return initializedContainerId
  } catch {
    return null
  }
}

export function pushGtmEvent(event, params) {
  try {
    if (!initializedContainerId) {
      return false
    }

    if (typeof event !== 'string' || !GTM_EVENT_NAME_PATTERN.test(event.trim())) {
      return false
    }

    const payload = {
      event: event.trim(),
    }

    if (params && typeof params === 'object' && !Array.isArray(params)) {
      for (const [key, value] of Object.entries(params)) {
        if (key === 'event') {
          continue
        }
        payload[key] = value
      }
    }

    ensureDataLayer().push(payload)
    return true
  } catch {
    return false
  }
}

function isInitialRouterLocation(from) {
  return !from || from.matched?.length === 0
}

function pushVirtualPageView(to) {
  pushGtmEvent('virtual_page_view', {
    page_path: to.fullPath,
    page_location: window.location.href,
    page_title: document.title,
  })
}

export function trackGtmRouteChanges(router) {
  if (routeTrackingInstalled || !initializedContainerId || !router?.afterEach) {
    return
  }

  routeTrackingInstalled = true

  router.afterEach((to, from) => {
    if (isInitialRouterLocation(from)) {
      return
    }

    pushVirtualPageView(to)
  })
}
